/**
 * MSAL Configuration for Entra ID Authentication
 * Phase 6: Frontend Application
 *
 * Configures Microsoft Authentication Library (MSAL) for:
 * - Single sign-on with Azure Entra ID
 * - Token acquisition for API calls
 * - Silent token refresh
 */

import type { Configuration, RedirectRequest, SilentRequest } from '@azure/msal-browser';

// Environment variables for auth configuration
const ENTRA_CLIENT_ID = process.env.NEXT_PUBLIC_ENTRA_CLIENT_ID || '';
const ENTRA_TENANT_ID = process.env.NEXT_PUBLIC_ENTRA_TENANT_ID || '';
const REDIRECT_URI = process.env.NEXT_PUBLIC_REDIRECT_URI || 'http://localhost:3000';
const API_SCOPE = process.env.NEXT_PUBLIC_API_SCOPE || `api://${ENTRA_CLIENT_ID}/access_as_user`;

/**
 * MSAL Configuration
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: ENTRA_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${ENTRA_TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'localStorage', // localStorage persists across tabs (sessionStorage does not)
    storeAuthStateInCookie: false, // Set to true for IE11/Edge legacy
  },
  system: {
    loggerOptions: {
      logLevel: process.env.NODE_ENV === 'development' ? 3 : 0, // 3 = Info, 0 = Error only
      piiLoggingEnabled: false, // Never log PII
    },
    allowRedirectInIframe: false,
  },
};

/**
 * Scopes for API access
 * These scopes are requested when acquiring tokens for the Recipe Manager API
 */
export const apiScopes = {
  // Main API access scope
  accessAsUser: API_SCOPE,
};

/**
 * Scopes to request at login
 * User.Read is required for getting user profile
 * Note: Group claims are configured in App Registration Token Configuration,
 * not requested as scopes
 */
export const loginScopes: string[] = [
  'openid',
  'profile',
  'email',
  'offline_access', // Required for refresh tokens
];

/**
 * Entra ID Security Group IDs
 * These must match the Object IDs of groups created in Entra ID
 * Configure in environment variables for each environment
 */
export const SECURITY_GROUPS = {
  // Users group - can view recipes
  USERS: process.env.NEXT_PUBLIC_ENTRA_GROUP_USERS || '',
  // Trusted Users group - can edit, create, and use LLM features
  TRUSTED_USERS: process.env.NEXT_PUBLIC_ENTRA_GROUP_TRUSTED_USERS || '',
} as const;

/**
 * Login request configuration
 */
export const loginRequest: RedirectRequest = {
  scopes: loginScopes,
};

/**
 * Token request for API calls
 */
export const apiTokenRequest: SilentRequest = {
  scopes: [apiScopes.accessAsUser],
  forceRefresh: false,
};

/**
 * Check if auth is properly configured
 */
export function isAuthConfigured(): boolean {
  return Boolean(ENTRA_CLIENT_ID && ENTRA_TENANT_ID);
}

/**
 * Get display-friendly auth config status (for debugging)
 */
export function getAuthConfigStatus(): {
  configured: boolean;
  clientId: string;
  tenantId: string;
  redirectUri: string;
} {
  return {
    configured: isAuthConfigured(),
    clientId: ENTRA_CLIENT_ID ? `${ENTRA_CLIENT_ID.substring(0, 8)}...` : 'NOT SET',
    tenantId: ENTRA_TENANT_ID ? `${ENTRA_TENANT_ID.substring(0, 8)}...` : 'NOT SET',
    redirectUri: REDIRECT_URI,
  };
}
